import { z } from 'zod';

import { AiCapabilitySchema } from './ai-capability/ai-capability';
import { AiFeatureKeySchema } from './ai-feature-key/ai-feature-key';

/**
 * The usage/cost dashboard (`03-ai-platform.md` §admin surface).
 *
 * Every figure is derived from `ai_usage_logs`, which is the same source the
 * budget guard falls back to — so the number on the dashboard and the number
 * the guard enforces cannot disagree.
 */

/** One day's roll-up, for the spend-over-time chart. */
export const AiUsageDailyPointSchema = z
  .object({
    /** `YYYY-MM-DD`, in the platform's active timezone. */
    day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    calls: z.number().int().min(0),
    cacheHits: z.number().int().min(0),
    costUsd: z.number().min(0),
  })
  .strict();

export type AiUsageDailyPoint = z.infer<typeof AiUsageDailyPointSchema>;

/** One grouping row — by feature, or by provider+model. */
export const AiUsageBreakdownRowSchema = z
  .object({
    label: z.string(),
    calls: z.number().int().min(0),
    cacheHits: z.number().int().min(0),
    refusals: z.number().int().min(0),
    failures: z.number().int().min(0),
    inputTokens: z.number().int().min(0),
    outputTokens: z.number().int().min(0),
    costUsd: z.number().min(0),
  })
  .strict();

export type AiUsageBreakdownRow = z.infer<typeof AiUsageBreakdownRowSchema>;

export const AiUsageStatsSchema = z
  .object({
    /** The window these figures cover, echoed back so a chart can label itself. */
    from: z.iso.datetime(),
    to: z.iso.datetime(),

    calls: z.number().int().min(0),
    cacheHits: z.number().int().min(0),
    refusals: z.number().int().min(0),
    failures: z.number().int().min(0),
    /** `cacheHits / calls`, or 0 when there were no calls. */
    cacheHitRate: z.number().min(0).max(1),

    inputTokens: z.number().int().min(0),
    outputTokens: z.number().int().min(0),
    costUsd: z.number().min(0),

    /**
     * Spend against `ai.budget.monthlyUsd` for the **current calendar month**,
     * regardless of the requested window — the number that decides whether the
     * platform is about to start refusing.
     */
    monthToDateCostUsd: z.number().min(0),
    monthlyBudgetUsd: z.number().min(0),

    /** Latency of real provider calls only; cache hits would flatter both. */
    latencyP50Ms: z.number().int().min(0),
    latencyP95Ms: z.number().int().min(0),

    byFeature: z.array(AiUsageBreakdownRowSchema),
    byModel: z.array(AiUsageBreakdownRowSchema),
    daily: z.array(AiUsageDailyPointSchema),

    /**
     * Models seen in the window with no `ai.pricing` entry, whose cost was
     * charged at the deliberately expensive fallback rate.
     *
     * On the dashboard rather than only in a log, because an unpriced model
     * quietly inflating the budget for a month is exactly the failure the
     * fallback exists to make visible.
     */
    unpricedModels: z.array(z.string()),
  })
  .strict();

export type AiUsageStats = z.infer<typeof AiUsageStatsSchema>;

/** The window and grouping the dashboard asks for. */
export const AiUsageStatsQuerySchema = z
  .object({
    from: z.iso.datetime().optional(),
    to: z.iso.datetime().optional(),
    featureKey: AiFeatureKeySchema.optional(),
    capability: AiCapabilitySchema.optional(),
  })
  .strict();

export type AiUsageStatsQuery = z.infer<typeof AiUsageStatsQuerySchema>;
